import * as lo_ from 'lodash';
import { isArray } from '../../../models/utils';
import { Cluster } from '../../../models/cluster';
import { Comparison } from '../../../models/comparison';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { Corpus } from '../../../models/corpus';
import { Custodian } from '../../../models/custodian';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { DynamicFolder } from '../../../models/dynamic-folder';
import { Endpoint, EntityType, HelpIndex, UpdateStatusType } from '../../../models/enums';
import { ExportStream } from '../../../models/export-stream';
import { GenericColDef, GridContext, GridRowSelectionType, SortByFilter } from '../../../models/grid-context';
import { Profile } from '../../../models/profile';
import { ProfileHistory } from '../../../models/profile-history';
import { SavedSearch } from '../../../models/saved-searches';
import { SearchResult } from '../../../models/search-result';
import { View } from '../../../models/view';
import { Workflow } from '../../../models/workflow';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { SummaryColumnInfo } from './summary-column-info';
import { MediaID } from '../../../models/mediaid';
import { Batch } from '../../../models/batch';

export class SummaryData {
    isValid = false;
    gridName = 'Summary';
    automationId = 'summary';
    helpIndex: HelpIndex;
    updateStatusType: UpdateStatusType;
    columnDefs: Array<GenericColDef>;
    rowSelection = GridRowSelectionType.none;
    requestInfo: DrRequestInfo;
    responseArrayCallback: (value: any) => void;
    gridContext: GridContext;
    pagerVisible = true;
    pagerDisabled = false;
    contextMenuOptions: Array<ContextMenuOption>;

    constructor(node: GenericTreeNode, value: Partial<SummaryData>) {
        if (node && value) {
            Object.assign(this, value);

            // add auto ids
            if (this.columnDefs) {
                this.columnDefs.forEach(colDef => {
                    colDef.automationId = this.automationId;
                });
            }

            // check request data
            if (this.requestInfo && this.requestInfo.endpoint && this.requestInfo.data) {
                this.isValid = true;

                // remove visibility filders
                if (isArray(this.requestInfo.data.filters)) {
                    const filter = (this.requestInfo.data.filters as Array<any>).find(f => f.attribute === 'visible');
                    if (filter) {
                        this.requestInfo.data.filters.splice(this.requestInfo.data.filters.indexOf(filter), 1);
                        if (this.requestInfo.data.filters.length === 0) {
                            this.requestInfo.data.filters = undefined;
                        }
                    }
                }
            }
        }
    }
}

export const SummaryInfo: ReadonlyMap<EntityType, (node: GenericTreeNode) =>
    SummaryData> = new Map<EntityType, (node: GenericTreeNode) => SummaryData>([
        [EntityType.import, (node) => new SummaryData(node, {
            gridName: 'Imports',
            automationId: 'import-summary',
            updateStatusType: UpdateStatusType.CORPUS,
            helpIndex: HelpIndex.imports_datasets_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.import),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new Corpus(value),
            gridContext: new GridContext({ contextMenuItemMap: (item: Corpus, handler?: any) => node.createChild(item, handler) }),
            contextMenuOptions: node.createChild({}).contextMenuOptions
        })],
        [EntityType.export, (node) => new SummaryData(node, {
            gridName: 'Export Streams',
            automationId: 'exports-summary',
            updateStatusType: UpdateStatusType.EXPORT_VIEW,
            helpIndex: HelpIndex.exports_ediscexports_summ_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.export),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new ExportStream(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: ExportStream, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.NAME,
                sortAscending: true,
                sortByFieldName: 'sortByFilter',
                sortEnabled: false
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions
        })],
        [EntityType.custodian, (node) => new SummaryData(node, {
            gridName: 'Custodians',
            automationId: 'custodians-summary',
            updateStatusType: UpdateStatusType.CUSTODIAN,
            helpIndex: HelpIndex.myedisc_custodian_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.custodian),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new Custodian(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: Custodian, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.SIZE,
                sortAscending: true,
                sortAscendingFieldName: 'descending',
                sortByFieldName: 'sortByFilter',
                inlineGridFilter: true
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
        })],
        [EntityType.mediaID, (node) => new SummaryData(node, {
            gridName: 'MediaIDs',
            automationId: 'mediaids-summary',
            updateStatusType: UpdateStatusType.MEDIAID,
            helpIndex: HelpIndex.mediaidview_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.mediaID),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new MediaID(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: MediaID, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.SIZE,
                sortAscending: true,
                sortAscendingFieldName: 'descending',
                sortByFieldName: 'sortByFilter',
                inlineGridFilter: true
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions
        })],
        [EntityType.batch, (node) => new SummaryData(node, {
            gridName: 'Batches',
            automationId: 'batches-summary',
            updateStatusType: UpdateStatusType.BATCH,
            helpIndex: HelpIndex.batchview_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.batch),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new Batch(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: Batch, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.SIZE,
                sortAscending: true,
                sortAscendingFieldName: 'descending',
                sortByFieldName: 'sortByFilter',
                inlineGridFilter: true
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions
        })],
        [EntityType.folder, (node) => new SummaryData(node, {
            gridName: 'Folders',
            automationId: 'folder-summary',
            updateStatusType: UpdateStatusType.DYNAMIC_FOLDER_VIEW,
            helpIndex: HelpIndex.myedisc_folders_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.folder),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new DynamicFolder(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: DynamicFolder, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.NAME,
                sortAscending: true,
                sortByFieldName: 'sortByFilter',
                sortAscendingFieldName: 'descending',
                inlineGridFilter: true
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
        })],
        [EntityType.tagView, (node) => new SummaryData(node, {
            gridName: 'Tags',
            automationId: 'tags-summary',
            updateStatusType: UpdateStatusType.TAG_VIEW,
            helpIndex: HelpIndex.myedisc_tags_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.tagView),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new View(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: View, handler?: any) => node.createChild(item, handler),
                inlineGridFilter: true,
                sortBy: SortByFilter.NAME,
                sortByFieldName: 'sortByFilter',
                sortAscendingFieldName: 'descending',
                sortAscending: true
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
        })],
        [EntityType.savedSearch, (treeItem) => new SummaryData(treeItem, {
            gridName: 'Saved Searches',
            automationId: 'saved-searches-summary',
            updateStatusType: UpdateStatusType.SAVED_SEARCH,
            helpIndex: HelpIndex.myedisc_savedsearchview_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.savedSearch),
            rowSelection: GridRowSelectionType.multipleCheckbox,
            requestInfo: lo_.cloneDeep(treeItem.requestInfo),
            responseArrayCallback: (value: any) => new SavedSearch(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: SavedSearch, handler?: any) => treeItem.createChild(item, handler),
                sortTypeEnabled: false,
                sortEnabled: false
            }),
            contextMenuOptions: treeItem.createChild({}).contextMenuOptions,
        })],
        [EntityType.searchResult, (treeItem) => new SummaryData(treeItem, {
            gridName: 'Search Results',
            automationId: 'search-results-summary',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.myedisc_searchhistoryview_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.searchResult),
            rowSelection: GridRowSelectionType.multipleCheckboxWithRowClickSelection,
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.projectManager_listSearchHistory,
                responseName: 'views',
                data: {
                    projectHandle: treeItem.requestInfo.data.projectHandle,
                    filters: [{ attribute: 'docBasedResultsOnly', value: 'true', operator: 'EQUALS' }],
                    selectedAttributes: [],
                    startIndex: 0,
                    count: 10
                }
            }),          // lo_.cloneDeep(treeItem.requestInfo),
            responseArrayCallback: (value: any) => new SearchResult(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: SearchResult, handler?: any) => treeItem.createChild(item, handler),
                sortTypeEnabled: false,
                sortEnabled: false,
                inlineGridFilter: true
            }),
            contextMenuOptions: treeItem.createChild({}).contextMenuOptions
        })],
        [EntityType.profile, (node) => new SummaryData(node, {
            gridName: 'Synthetic Document',
            automationId: 'synthetic-document-summary',
            updateStatusType: UpdateStatusType.PROFILE,
            helpIndex: HelpIndex.myedisc_profiles_summary,
            columnDefs: SummaryColumnInfo.get(EntityType.profile),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new Profile(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: Profile, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.NAME,
                sortTypeEnabled: false,
                sortAscending: true,
                sortByFieldName: 'sortByFilter',
                sortEnabled: false
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
        })],
        [EntityType.profileHistory, (node) => new SummaryData(node, {
            gridName: 'Synthetic Document History',
            automationId: 'synthetic-document-history-summary',
            updateStatusType: UpdateStatusType.PROFILE,
            helpIndex: HelpIndex.myedisc_profile_history_tab_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.profileHistory),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new ProfileHistory(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: ProfileHistory, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.DATE_CREATED,
                sortTypeEnabled: false,
                sortAscending: false,
                sortByFieldName: 'sortByFilter',
                sortEnabled: false
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
        })],
        [EntityType.comparison, (node) => new SummaryData(node, {
            gridName: 'Comparison',
            automationId: 'comparison-summary',
            updateStatusType: UpdateStatusType.COMPARISON,
            helpIndex: HelpIndex.myedisc_comparisons_summary_dlg,
            columnDefs: SummaryColumnInfo.get(EntityType.comparison),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new Comparison(value),
            gridContext: new GridContext({
                contextMenuItemMap: (item: Comparison, handler?: any) => node.createChild(item, handler),
                sortBy: SortByFilter.NAME,
                sortTypeEnabled: false,
                sortAscending: true,
                sortByFieldName: 'sortByFilter',
                sortEnabled: false
            }),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
        })],
        [EntityType.cluster, (node) => new SummaryData(node, {
            gridName: 'Clusters',
            automationId: 'clusters-summary',
            updateStatusType: UpdateStatusType.CLUSTER_VIEW,
            rowSelection: GridRowSelectionType.single,
            columnDefs: SummaryColumnInfo.get(EntityType.cluster),
            requestInfo: Object.assign({}, new DrRequestInfo({
                endpoint: Endpoint.clusterViewManager_listChildViews,
                responseName: 'childViews',
                data: {
                    parentClusterViewHandle: node.listingHandle,
                    count: 100,
                    startIndex: 0
                }
            })),
            responseArrayCallback: (value: any) => new Cluster(value)
        })],
        [EntityType.workFlow, (node) => new SummaryData(node, {
            gridName: 'Workflows',
            automationId: 'workflows-summary',
            updateStatusType: UpdateStatusType.WORK_FLOW,
            helpIndex: HelpIndex.myedisc_workflows_summary_dlg,
            rowSelection: GridRowSelectionType.none,
            columnDefs: SummaryColumnInfo.get(EntityType.workFlow),
            contextMenuOptions: node.createChild({}).contextMenuOptions,
            gridContext: new GridContext({ contextMenuItemMap: (item: Workflow, handler?: any) => node.createChild(item, handler) }),
            requestInfo: lo_.cloneDeep(node.requestInfo),
            responseArrayCallback: (value: any) => new Workflow(value),
        })]
    ]);
